export function capitalizeString(inputString?: string) {
  if (!inputString) return "";
  return inputString
    ? inputString.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
    : "";
}

export function capitalizeWordsSeperatedByUnderScore(input: string): string {
  const words = input.split("_");
  const capitalizedWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1),
  );
  const result = capitalizedWords.join(" ");
  return result;
}

/**
 * Capitalizes the first letter of each word in a string separated by spaces.
 *
 * @param {string} input - The input string to be capitalized.
 * @returns {string} - The capitalized string.
 */
export function capitalizeWordsSeparatedBySpace(input: string): string {
  return input
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function replaceSemicolonWithBr(text) {
  return text.replace(/;/g, "<br />");
}

export function replaceBrWithSemicolon(text) {
  return text.replace(/<br\s*\/?>/gi, ";");
}

/**
 * Format a raw phone value into a US-style `(XXX) XXX-XXXX` string.
 *
 * Accepts non-string inputs (number, null, undefined) defensively by coercing
 * to a string first, so callers passing numeric or empty values won't throw.
 *
 * @param value - The raw phone value to format
 * @returns The progressively formatted phone number string
 */
export const formatPhoneNumber = (value: string | number | null | undefined) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const unformatPhoneNumber = (formattedValue: string) => {
  return formattedValue.replace(/\D/g, ""); // Remove all non-digit characters
};

export const formatPhoneNumberWithCountryCode = (value: string) => {
  const digits = value.replace(/\D/g, "");
  // Handle different country codes
  if (digits.startsWith("1") && digits.length > 10) {
    // USA/Canada (Country Code +1)
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  } else if (digits.startsWith("44") && digits.length > 10) {
    // UK (Country Code +44)
    return `+44 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 12)}`;
  } else if (digits.startsWith("91") && digits.length >= 10) {
    // India (Country Code +91) - Corrected format
    return `+91 ${digits.slice(2, 6)}-${digits.slice(6, 12)}`;
  } else if (digits.length > 10) {
    // Generic International Format
    return `+${digits.slice(0, digits.length - 10)} (${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`;
  }

  // Default formatting for 10-digit numbers (assumed US format)
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Helper function to get the appropriate display text for audit trail actions.
 * If the name is "All", it returns an empty string to avoid displaying "All".
 *
 * @param {string} name - The name field from the event
 * @returns {string} - The appropriate text to display
 */
export const getAuditTrailDisplayText = (name?: string): string => {
  if (name === "All") {
    return "";
  }
  return name || "";
};

/**
 * Checks if a string is empty, contains only whitespace, or is null/undefined.
 * @param inputString - The string to validate.
 * @returns `true` if the string is empty, whitespace-only, null, or undefined; otherwise `false`.
 */
export const checkIfStringIsEmpty = (inputString?: string): boolean => {
  return (
    inputString === "" ||
    inputString?.trim() === "" ||
    inputString === null ||
    inputString === undefined
  );
};

/**
 * Performs a case-insensitive substring match between text and query.
 * Returns true if the query is empty/whitespace or if the query appears as a substring in the text.
 * @param text - The text to search in
 * @param query - The search query
 * @returns `true` if query is empty or if query is found in text (case-insensitive); otherwise `false`
 */
export const substringMatch = (text: string, query: string): boolean => {
  if (!query.trim()) return true;

  const normalizedText = text.toLowerCase();
  const normalizedQuery = query.toLowerCase().trim();

  return normalizedText.includes(normalizedQuery);
};
